import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
// @mui
import { alpha } from '@mui/material/styles';
import { Box, Button, Card, Grid, Link, Stack, Typography } from '@mui/material';
// components
import Iconify from '../../../components/iconify';
import { MotionInView, ButtonAnimate, varFadeInUp } from '../../../components/animate';
// data
import { CONTACT_ROWS, CONTACT_EMAIL, mailHref } from '../../../data/contact-info';

// ----------------------------------------------------------------------

ContactLocationRow.propTypes = {
  locations: PropTypes.object.isRequired,
};

function ContactLocationRow({ locations }) {
  return (
    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          flexShrink: 0,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          color: 'primary.main',
          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
        }}
      >
        <Iconify icon={locations.icon} width={19} />
      </Box>

      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
          {locations.label}
        </Typography>

        <Stack spacing={{ xs: 1.5, sm: 2 }} sx={{ mt: 1 }}>
          {locations.details.map((detail) => (
            <Stack key={detail.id} direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
              <Iconify icon={detail.flag} width={18} sx={{ mt: 0.3, flexShrink: 0 }} />

              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 700 }}>
                  {detail.name}
                </Typography>

                <Link
                  href={detail.mapHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={{ color: 'text.secondary', fontSize: 14, display: 'block', lineHeight: 1.65 }}
                >
                  {detail.address}
                </Link>

                <Link
                  href={detail.phoneHref}
                  underline="hover"
                  sx={{ color: 'text.secondary', fontSize: 14, display: 'block', width: 'fit-content' }}
                >
                  {detail.phone}
                </Link>

                <Stack direction="row" useFlexGap spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                  <Button
                    component="a"
                    href={detail.phoneHref}
                    size="small"
                    variant="outlined"
                    startIcon={<Iconify icon="ic:round-call" sx={{ width: 16, height: 16 }} />}
                  >
                    Call
                  </Button>

                  <Button
                    component="a"
                    href={detail.whatsappHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    variant="outlined"
                    startIcon={<Iconify icon="ic:baseline-whatsapp" sx={{ width: 16, height: 16 }} />}
                    sx={{ color: '#128C7E', borderColor: '#25D366' }}
                  >
                    WhatsApp
                  </Button>
                </Stack>
              </Box>
            </Stack>
          ))}
        </Stack>
      </Box>
    </Stack>
  );
}

ContactEmailRow.propTypes = {
  emails: PropTypes.object.isRequired,
};

function ContactEmailRow({ emails }) {
  return (
    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          flexShrink: 0,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          color: 'primary.main',
          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
        }}
      >
        <Iconify icon={emails.icon} width={19} />
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
          {emails.label}
        </Typography>

        <Stack direction="row" useFlexGap spacing={{ xs: 0.5, sm: 1.5 }} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
          {emails.datas.map((data) => (
            <Link
              key={data.label}
              href={data.href}
              underline="hover"
              sx={{ color: 'text.secondary', fontSize: 14 }}
            >
              {data.label}
            </Link>
          ))}
        </Stack>
      </Box>
    </Stack>
  );
}

// ----------------------------------------------------------------------

export default function ContactWidget() {
  return (
    <Card
      component="section"
      aria-labelledby="contact-widget-heading"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 3,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        boxShadow: (theme) => `0 20px 50px ${alpha(theme.palette.grey[900], 0.1)}`,
        backgroundImage: (theme) =>
          `radial-gradient(circle at 0% 0%, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 42%)`,
      }}
    >
      <Grid container spacing={0} sx={{ alignItems: 'stretch' }}>
        <Grid size={{ xs: 12, md: 7 }} sx={{ p: { xs: 2.5, sm: 3.5, md: 5 } }}>
          <MotionInView variants={varFadeInUp}>
            <Typography
              variant="subtitle2"
              sx={{ color: '#DF3E30', mb: 1, textTransform: 'uppercase', letterSpacing: 1.2 }}
            >
              Contact us
            </Typography>

            <Typography
              id="contact-widget-heading"
              component="h2"
              variant="h3"
              sx={{ color: 'text.primary', mb: 1.5, fontSize: { xs: '1.75rem', md: '2.35rem' } }}
            >
              Let&apos;s plan your next step
            </Typography>

            <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8, maxWidth: 560 }}>
              Have a question about our courses, licensing routes or technical support? Reach out through the channel
              that suits you. Our team is ready to help.
            </Typography>
          </MotionInView>

          <MotionInView variants={varFadeInUp} transition={{ delay: 0.1 }}>
            <Stack
              spacing={{ xs: 2, md: 2.5 }}
              sx={{
                mt: { xs: 2.5, md: 3 },
                p: { xs: 2, sm: 2.5 },
                borderRadius: 2.5,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                bgcolor: (theme) => alpha(theme.palette.background.neutral, 0.62),
              }}
            >
              {CONTACT_ROWS.map((row, index) => (
                <Stack key={index} spacing={{ xs: 2, md: 2.5 }}>
                  <ContactLocationRow locations={row.locations} />
                  <ContactEmailRow emails={row.emails} />
                </Stack>
              ))}
            </Stack>
          </MotionInView>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: { xs: 1.5, sm: 2 },
              mt: { xs: 3, md: 3.5 },
              alignItems: 'center',
            }}
          >
            <ButtonAnimate mediumClick>
              <Button
                component={RouterLink}
                to="/contact-us"
                size="large"
                variant="contained"
                startIcon={<Iconify icon="ic:round-message" sx={{ width: 20, height: 20 }} />}
                sx={{
                  borderRadius: 2.5,
                  px: { xs: 2.5, sm: 3.5 },
                  py: 1.25,
                  boxShadow: (theme) => `0 10px 30px ${alpha(theme.palette.primary.main, 0.35)}`,
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: (theme) => `0 14px 36px ${alpha(theme.palette.primary.main, 0.45)}`,
                  },
                  transition: 'all 0.25s ease',
                }}
              >
                Send an enquiry
              </Button>
            </ButtonAnimate>

            <ButtonAnimate mediumClick>
              <Button
                component="a"
                href={mailHref(CONTACT_EMAIL)}
                size="large"
                variant="outlined"
                startIcon={<Iconify icon="ic:round-mail-outline" sx={{ width: 20, height: 20 }} />}
                sx={{
                  borderRadius: 2.5,
                  px: { xs: 2.5, sm: 3.5 },
                  py: 1.25,
                  borderColor: (theme) => alpha(theme.palette.primary.main, 0.5),
                  color: 'primary.main',
                  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.25s ease',
                }}
              >
                Email us
              </Button>
            </ButtonAnimate>
          </Box>
        </Grid>

        {/* The supporting visual remains visible at every breakpoint. */}
        <Grid
          size={{ xs: 12, md: 5 }}
          sx={{
            display: 'flex',
            position: 'relative',
            overflow: 'hidden',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: { xs: 280, sm: 360, md: 520 },
            p: { xs: 2.5, sm: 3.5, md: 3.5, lg: 5 },
            bgcolor: 'grey.900',
            backgroundImage: (theme) =>
              `radial-gradient(circle at 80% 10%, ${alpha(theme.palette.primary.main, 0.55)} 0%, transparent 38%), radial-gradient(circle at 10% 90%, ${alpha('#1877F2', 0.42)} 0%, transparent 38%)`,
          }}
        >
          <MotionInView variants={varFadeInUp} sx={{ width: '100%', position: 'relative', zIndex: 1 }}>
            <Box
              sx={{
                borderRadius: 3,
                p: { md: 2.5, lg: 3.5 },
                textAlign: 'center',
                border: `1px solid ${alpha('#FFFFFF', 0.16)}`,
                backgroundColor: alpha('#FFFFFF', 0.08),
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
              }}
            >
              <Typography
                variant="overline"
                sx={{ display: 'block', color: 'primary.light', letterSpacing: 1.2, mb: 1 }}
              >
                Sharjah &bull; Kuttippuram
              </Typography>
              <Box
                component="img"
                src="/assets/illustrations/illustration_contact.png"
                alt="Contact ASAP Marketing and Studies Consultancy"
                loading="lazy"
                decoding="async"
                sx={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: 340,
                  objectFit: 'contain',
                  display: 'block',
                  mx: 'auto',
                }}
              />
              <Typography variant="subtitle1" sx={{ color: 'common.white', mt: 1.5, fontWeight: 700 }}>
                Guidance for your licensing journey
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: (theme) => alpha(theme.palette.common.white, 0.68), mt: 0.75 }}
              >
                Speak with our team about courses, exam preparation and application support.
              </Typography>
            </Box>
          </MotionInView>
        </Grid>
      </Grid>
    </Card>
  );
}
